import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';
import { useEffect, useMemo, useState } from 'react';
import { FaFileCsv } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const getColor = (margin: number): string => {
    if (margin < 5) return '#d73027'; // Very risky
    if (margin < 15) return '#fc8d59'; // At risk
    if (margin < 30) return '#fee08b'; // Medium
    if (margin < 50) return '#91cf60'; // Good
    return '#1a9850'; // Excellent
};

const DownloadCSVButton = ({
    data,
    filename = 'export-marges.csv',
}: {
    data: any[];
    filename?: string;
}) => {
    const downloadCSV = () => {
        if (!data || data.length === 0) return;

        const csvHeaders = Object.keys(data[0]).join(';');
        const csvRows = data.map((row) =>
            Object.values(row)
                .map((value) => {
                    const stringValue = String(value ?? '');
                    const escaped = stringValue.replace(/"/g, '""');
                    return `"${escaped}"`;
                })
                .join(';'),
        );

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div
            onClick={downloadCSV}
            className="absolute right-4 top-4 cursor-pointer rounded-full bg-lightPrimary p-2 text-gray-600 hover:bg-gray-200 dark:text-white dark:hover:bg-gray-700"
        >
            <FaFileCsv size={20} />
        </div>
    );
};

// Number of lines rendered simultaneously in the DOM. Essential for
// maintaining smooth performance with thousands of products (otherwise ~1000 lines × 5 cells).
const PAGE_SIZE = 50;

const ListMargin = (props: { data: any }) => {
    const { data } = props;
    const [period, setPeriod] = useState('descendant');
    const nav = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);

    // Filter + sort memoized: recalculated only when the data, the
    // The search or sorting direction changes. We copy the table before sorting.
    // to NEVER mutate the received prop (source of bugs and re-rendering).
    // Access to `.toLowerCase()` is secure: only one product per `name`/`ref`
    // null used to crash the entire list.
    const processedData = useMemo(() => {
        if (!data || !data.length) return [];
        const query = searchQuery.trim().toLowerCase();
        let rows = data;
        if (query) {
            rows = rows.filter(
                (item: any) =>
                    (item.ref || '').toLowerCase().includes(query) ||
                    (item.name || '').toLowerCase().includes(query),
            );
        }
        return [...rows].sort((a: any, b: any) =>
            period === 'ascendant'
                ? a.percentage - b.percentage
                : b.percentage - a.percentage,
        );
    }, [data, searchQuery, period]);

    const pageCount = Math.max(1, Math.ceil(processedData.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount - 1);

    // Only the lines from the current page are injected into the DOM.
    const visibleRows = useMemo(
        () =>
            processedData.slice(
                currentPage * PAGE_SIZE,
                currentPage * PAGE_SIZE + PAGE_SIZE,
            ),
        [processedData, currentPage],
    );

    useEffect(() => {
        setPage(0);
    }, [searchQuery, period]);

    return (
        <Card
            extra={
                'w-full p-5 h-fit bg-blue-200 h-[320px] shadow shadow-3xl border ma-h'
            }
        >
            <p className="mb-2 text-base font-bold text-navy-700 dark:text-white">
                Marges par produit
            </p>
            <p className="mb-2 text-sm font-normal text-gray-600 dark:text-white">
                Liste des marges par sur les 12 derniers mois.
            </p>
            <DownloadCSVButton data={data} />
            <div className="flex gap-2">
                <div className="flex h-[35px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Produit ou référence..."
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-[450px] text-sm block rounded-full bg-lightPrimary text-navy-700 outline-none"
                    />
                </div>
                {/* <div className="">
                    <SearchSelector
                        displayKey="company"
                        options={[
                            { _id: 'all', company: 'Tous les clients' },
                            ...(filteredCustomersWithOrders(customers) || []),
                        ]}
                        value={customerId}
                        onSelect={(e: any) => {
                            setCustomerId(e._id);
                        }}
                        inputSmall={true}
                    />
                </div> */}
            </div>
            <div className="absolute right-[70px] top-4">
                <div className="flex mt-2 gap-4">
                    <p
                        className={`text-sm ${
                            period === 'descendant'
                                ? 'text-toola-500 underline'
                                : ''
                        } cursor-pointer`}
                        onClick={() => setPeriod('descendant')}
                    >
                        Décroissant
                    </p>
                    <p
                        className={`text-sm ${
                            period === 'ascendant'
                                ? 'text-toola-500 underline'
                                : ''
                        } cursor-pointer`}
                        onClick={() => setPeriod('ascendant')}
                    >
                        Croissant
                    </p>
                </div>
            </div>
            {data ? (
                data.length > 0 ? (
                    <>
                        <div className="mt-2 w-full h-[175px] overflow-y-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left h-[24px]">
                                        <th
                                            className="pl-[10px] w-[50%] sm:w-[35%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                            rowSpan={2}
                                        >
                                            Produit
                                        </th>
                                        <th
                                            className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                            rowSpan={2}
                                        >
                                            P. d'achat moyen (€)
                                        </th>
                                        <th
                                            className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                            rowSpan={2}
                                        >
                                            P. de vente moyen (€)
                                        </th>
                                        <th
                                            className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                            rowSpan={2}
                                        >
                                            Marge moyenne (€)
                                        </th>
                                        <th
                                            className="hidden sm:table-cell pl-[10px] w-[15%] text-xs sm:text-sm text-gray-600 dark:text-white font-medium border-b border-[#E9EDF7] align-bottom"
                                            rowSpan={2}
                                        >
                                            T. Marque moyen (%)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map(
                                        (row: any, index: number) => {
                                            return (
                                                <tr
                                                    key={row._id || index}
                                                    className="text-left h-[24px] border-t border-[#E9EDF7] sm:border-none"
                                                >
                                                    <td className="text-sm font-medium pl-[10px] text-toola-800">
                                                        <p
                                                            className="flex gap-2 text-toola-400 cursor-pointer hover:underline"
                                                            onClick={() =>
                                                                nav(
                                                                    `/update-product/${row._id}`,
                                                                )
                                                            }
                                                        >
                                                            {row.name
                                                                ? row.name.toLowerCase()
                                                                : 'N/A'}{' '}
                                                        </p>
                                                        <div className="sm:hidden font-normal">
                                                            {row.sum +
                                                                ' ' +
                                                                row.unity}
                                                            <br />
                                                            {row.totalPrice}€
                                                        </div>
                                                    </td>
                                                    <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                        {row.averagePurchasePrice?.toFixed(
                                                            2,
                                                        )}
                                                        €
                                                    </td>
                                                    <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                        {row.averageSellPrice?.toFixed(
                                                            2,
                                                        )}
                                                        €
                                                    </td>
                                                    <td className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800">
                                                        {row.marginPerUnit?.toFixed(
                                                            2,
                                                        )}
                                                        €
                                                    </td>
                                                    <td
                                                        className="text-sm font-medium hidden sm:table-cell pl-[10px] text-toola-800"
                                                        style={{
                                                            color:
                                                                row.percentage !=
                                                                null
                                                                    ? getColor(
                                                                          row.percentage,
                                                                      )
                                                                    : '#6b7280',
                                                        }}
                                                    >
                                                        {row.percentage
                                                            ? row.percentage?.toFixed(
                                                                  2,
                                                              )
                                                            : 'N/A'}{' '}
                                                        %
                                                    </td>
                                                </tr>
                                            );
                                        },
                                    )}
                                </tbody>
                            </table>
                            {processedData.length === 0 && (
                                <div className="w-full flex items-center justify-center h-[50px]">
                                    <p className="text-gray-700">
                                        Aucun produit ne correspond à la
                                        recherche
                                    </p>
                                </div>
                            )}
                        </div>
                        {pageCount > 1 && (
                            <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                                <span>{processedData.length} produits</span>
                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        disabled={currentPage === 0}
                                        onClick={() =>
                                            setPage((p) => Math.max(0, p - 1))
                                        }
                                        className="rounded-md border border-gray-200 px-2 py-1 hover:bg-lightPrimary disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Précédent
                                    </button>
                                    <span>
                                        Page {currentPage + 1} / {pageCount}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={currentPage >= pageCount - 1}
                                        onClick={() =>
                                            setPage((p) =>
                                                Math.min(pageCount - 1, p + 1),
                                            )
                                        }
                                        className="rounded-md border border-gray-200 px-2 py-1 hover:bg-lightPrimary disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Suivant
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="w-full flex items-center justify-center h-[50px]">
                        <p className="text-gray-700">
                            Aucune marge à afficher
                        </p>
                    </div>
                )
            ) : (
                <div>
                    <p>Chargement...</p>
                </div>
            )}
        </Card>
    );
};

export default ListMargin;
